import React from 'react';
import { HistoryItem, Subtitle } from '../types';
import { Clock, Download, FileText, Trash2, Globe, Video, Edit3 } from 'lucide-react';
import { generateSrtContent, downloadSrt } from '../utils';

interface HistoryListProps {
  items: HistoryItem[];
  onDelete: (id: string, path: string) => void;
  onLoad: (item: HistoryItem) => void;
}

const HistoryList: React.FC<HistoryListProps> = ({ items, onDelete, onLoad }) => {
  
  const handleDownload = (item: HistoryItem, translated: boolean) => {
    const content = generateSrtContent(item.subtitles, translated);
    const suffix = translated ? `_${item.targetLanguage}` : '_original';
    const filename = `${item.originalName.split('.')[0]}${suffix}.srt`;
    downloadSrt(content, filename);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    // Handle Firestore Timestamp or Date object
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const calculateDaysLeft = (expiresAt: any) => {
    if (!expiresAt) return 0;
    const now = new Date();
    const expiry = expiresAt.seconds ? new Date(expiresAt.seconds * 1000) : new Date(expiresAt);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-500 border-2 border-dashed border-gray-800 rounded-3xl bg-gray-900/30">
        <Video className="w-12 h-12 mb-4 opacity-20" />
        <p>No history yet. Process a video to see it here.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map((item) => {
        const daysLeft = calculateDaysLeft(item.expiresAt);
        const isExpiringSoon = daysLeft <= 1;

        return (
          <div 
            key={item.id}
            className="group relative bg-gray-900/60 backdrop-blur-md border border-white/5 rounded-2xl p-5 hover:border-blue-500/30 transition-all duration-300 hover:bg-gray-800/80 shadow-lg"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="p-2.5 bg-blue-600/10 rounded-xl text-blue-400 group-hover:bg-blue-600/20 transition-colors">
                  <Video className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-gray-200 truncate pr-2" title={item.originalName}>
                    {item.originalName}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    {formatDate(item.createdAt)}
                  </div>
                </div>
              </div>
              <button 
                onClick={() => onDelete(item.id, item.storagePath)}
                className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center gap-2 mb-5">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-800 text-xs text-gray-400 border border-gray-700">
                <Globe className="w-3 h-3" /> {item.detectedLanguage} → {item.targetLanguage}
              </span>
              {daysLeft > 0 && (
                <span className={`text-[10px] px-2 py-1 rounded-md border ${isExpiringSoon ? 'bg-red-900/20 border-red-900 text-red-400' : 'bg-gray-800 border-gray-700 text-gray-400'}`}>
                  Expires in {daysLeft} days
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button 
                onClick={() => onLoad(item)}
                className="col-span-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium transition-colors border border-white/5"
              >
                <Edit3 className="w-3 h-3" /> Editor
              </button>
              <button 
                onClick={() => handleDownload(item, false)}
                className="col-span-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium transition-colors border border-white/5"
              >
                <FileText className="w-3 h-3" /> Orig
              </button>
              <button 
                onClick={() => handleDownload(item, true)}
                className="col-span-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-blue-600/10 hover:bg-blue-600/20 text-blue-300 text-xs font-medium transition-colors border border-blue-500/20"
              >
                <Download className="w-3 h-3" /> Trans
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HistoryList;